import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ForgotPasswordService } from '../../services/forgot-password.service';
import { MessageService } from 'primeng/api';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { UsuarioService } from '../../services/usuario/usuario.service';
import { UsuarioContextoDTO } from '../../models/usuario.model';
import { PermissaoService } from '../../services/permissao/permissao.service';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  providers: [MessageService]
})
export class ForgotPasswordComponent implements OnInit {

  step = 1;

  // Flags de loading por step (evitam múltiplos cliques e dão feedback visual)
  enviandoCodigo = false;     // Step 1: Enviar Código
  verificandoCodigo = false;  // Step 2: Verificar
  redefinindoSenha = false;   // Step 3: Redefinir Senha (cobre também o auto-login)

  // Step 1
  email: string = '';

  // Step 2
  verificationCode: string = ''; // 4 dígitos
  timer: number = 0;
  intervalId: any;
  resetToken: string = ''; // token de uso único obtido na validação do código

  // Step 3
  newPassword: string = '';
  confirmPassword: string = '';

  constructor(
    private router: Router,
    private forgotPasswordService: ForgotPasswordService,
    private messageService: MessageService,
    private authService: AuthService,
    private apiService: ApiService, private safe: SafeStorageService,
    private translate: TranslateService,
    private usuarioService: UsuarioService,
    private permissaoService: PermissaoService
  ) {}

  ngOnInit(): void {}

  // Step 1: Enviar código para redefinir senha
  sendCode(): void {
    if (!this.email) {
      this.showWarn(this.translate.instant('forgot-password.mensagens.preencha_email'));
      return;
    }
    this.enviandoCodigo = true;
    this.forgotPasswordService.gerarCodigoRedefinicaoSenha(this.email).subscribe({
      next: (res) => {
        // Backend sempre responde 200 (anti-enumeração): mensagem neutra,
        // não confirma se o e-mail está cadastrado.
        this.enviandoCodigo = false;
        this.showSuccess(this.translate.instant('forgot-password.mensagens.codigo_enviado_neutro'));
        this.step = 2;
        this.verificationCode = '';
        this.startTimer();
      },
      error: (err) => {
        this.enviandoCodigo = false;
        this.showError(this.translate.instant('forgot-password.mensagens.erro_enviar_codigo'));
        console.error(err);
      }
    });
  }

  // Step 2: Validar o código
  // validateCode(): void {
  //   if (!this.verificationCode || this.verificationCode.length < 8) {
  //     this.showWarn('Digite o código de 8 dígitos.');
  //     return;
  //   }
  //   this.forgotPasswordService.validarCodigoRedefinicaoSenha(this.email, this.verificationCode).subscribe({
  //     next: (res) => {
  //       this.showSuccess('Código validado com sucesso.');
  //       this.step = 3;
  //       this.stopTimer();
  //     },
  //     error: (err) => {
  //       this.showError('Código inválido ou expirado.');
  //       console.error(err);
  //     }
  //   });
  // }
  validateCode(): void {
    let code: any = this.verificationCode;
    if (Array.isArray(code)) {
      code = code.join('');
    }
    const codigoLimpo = String(code).replace(/[^0-9]/g, '');

    if (!codigoLimpo || codigoLimpo.length < 4) {
      this.showWarn(this.translate.instant('forgot-password.mensagens.digite_codigo'));
      return;
    }
    this.verificandoCodigo = true;
    this.forgotPasswordService.validarCodigoRedefinicaoSenha(this.email, codigoLimpo).subscribe({
      next: (res) => {
        this.verificandoCodigo = false;
        this.resetToken = res?.resetToken ?? '';
        this.showSuccess(this.translate.instant('forgot-password.mensagens.codigo_validado'));
        this.step = 3;
        this.stopTimer();
      },
      error: (err) => {
        this.verificandoCodigo = false;
        this.showError(this.translate.instant('forgot-password.mensagens.codigo_invalido'));
        console.error(err);
      }
    });
  }
  

  reSendCode(): void {
    this.forgotPasswordService.gerarCodigoRedefinicaoSenha(this.email).subscribe({
      next: (res) => {
        this.showSuccess(this.translate.instant('forgot-password.mensagens.novo_codigo_enviado'));
        this.startTimer();
      },
      error: (err) => {
        this.showError(this.translate.instant('forgot-password.mensagens.erro_reenviar_codigo'));
        console.error(err);
      }
    });
  }

  startTimer(): void {
    this.timer = 60;
    this.stopTimer();
    this.intervalId = setInterval(() => {
      this.timer--;
      if (this.timer <= 0) {
        this.stopTimer();
      }
    }, 1000);
  }

  stopTimer(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.intervalId = null;
  }

  goBackStep1(): void {
    this.stopTimer();
    this.step = 1;
  }

  // Step 3: Atualizar senha e login automático
  updatePassword(): void {
    if (!this.newPassword || !this.confirmPassword) {
      this.showWarn(this.translate.instant('forgot-password.mensagens.preencha_campos_senha'));
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.showWarn(this.translate.instant('forgot-password.mensagens.senhas_nao_conferem'));
      return;
    }
    // O loading permanece ativo durante a atualização da senha E o auto-login
    // seguinte (loginAfterReset). Só é desligado nos caminhos terminais.
    this.redefinindoSenha = true;
    this.forgotPasswordService.atualizarSenha(this.email, this.newPassword, this.resetToken).subscribe({
      next: (res) => {
        // Mantém redefinindoSenha = true: o fluxo continua no auto-login.
        this.showSuccess(this.translate.instant('forgot-password.mensagens.senha_atualizada'));
        this.loginAfterReset();
      },
      error: (err) => {
        this.redefinindoSenha = false;
        // 400: token ausente/inválido/expirado/já usado. O backend não detalha
        // o motivo no corpo, então orientamos a recomeçar solicitando novo código.
        if (err?.status === 400) {
          this.showError(this.translate.instant('forgot-password.mensagens.sessao_redefinicao_expirada'));
        } else {
          this.showError(this.translate.instant('forgot-password.mensagens.erro_atualizar_senha'));
        }
        console.error(err);
      }
    });
  }

  cancelReset(): void {
    this.step = 1;
  }

  loginAfterReset(): void {
    this.apiService
    .autenticar(this.email, this.newPassword)
    .subscribe(
      (response) => {
        const authHeader = response.headers.get('Authorization');
        if (authHeader) {
          this.safe.setItem('authToken', authHeader);
        }
        const userId = response.body.id;
        if (userId !== undefined && userId !== null) {
          this.safe.setItem('userId', String(userId));
        }

        // sessionStorage.id deve guardar empresaId (nao userId) - mesmo padrao
        // de login.component.ts e signup.component.ts. Buscar via /usuario/{id}.
        this.usuarioService.getContexto(`/usuario/${userId}`).subscribe({
          next: (usuario: UsuarioContextoDTO) => {
            const empresaId = usuario?.empresaId;
            if (empresaId != null) {
              this.safe.setItem('id', String(empresaId));
            }
            if (usuario?.email) {
              this.safe.setItem('email', usuario.email);
            }
            if (usuario?.categoriaAdmin != null) {
              this.safe.setItem('categoriaAdmin', String(usuario.categoriaAdmin));
            }
            if (usuario?.categoriaAcessoMaster != null) {
              this.safe.setItem('categoriaAcessoMaster', String(usuario.categoriaAcessoMaster));
            }
            this.authService.setAuthenticated(true);

            // Carregar permissoes e redirecionar inteligentemente (mesmo padrao
            // de login.component.ts e signup.component.ts). Sem isso o
            // permissionGuard fica aguardando indefinidamente e a navegacao
            // para o dashboard nunca conclui.
            const emailPermissoes = usuario?.email ?? this.email;
            this.permissaoService.carregarPermissoes(emailPermissoes).subscribe({
              next: () => {
                this.redefinindoSenha = false;
                this.messageService.add({
                  severity: 'success',
                  summary: this.translate.instant('login.mensagens.concluido'),
                  detail: this.translate.instant('login.mensagens.autenticado'),
                  life: 2000,
                });
                const rota = this.permissaoService.obterPrimeiraRotaAcessivel();
                this.router.navigateByUrl(rota);
              },
              error: () => {
                // Fallback: navegar para rota padrao mesmo se permissoes falharem
                this.redefinindoSenha = false;
                this.router.navigateByUrl('/');
              },
            });
          },
          error: () => {
            // Sem empresaId, autentica mesmo assim para nao bloquear o usuario;
            // proximas telas que dependem de sessionStorage.id podem falhar com
            // 404, mas a alternativa (bloquear) e pior.
            this.redefinindoSenha = false;
            this.authService.setAuthenticated(true);
            // Sem contexto do usuario nao ha email confiavel para buscar
            // permissoes: liberar permissao total no front (mesmo tratamento de
            // auth.service.ts bootstrapAuth) para nao travar o permissionGuard.
            this.permissaoService.carregarComPermissaoTotal();
            this.router.navigateByUrl('/');
          },
        });

      },
      (error) => {
        this.redefinindoSenha = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('login.mensagens.credenciais_invalidas'),
          life: 2000,
        });
        console.log(error);
      }
    );
  }

  backToLogin(): void {
    this.router.navigate(['/login']);
  }

  showSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail });
  }
  showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: this.translate.instant('common.erro'), detail });
  }
  showWarn(detail: string): void {
    this.messageService.add({ severity: 'warn', summary: this.translate.instant('common.atencao'), detail });
  }
}
